import { createSelector } from 'reselect';
import createCachedSelector from 're-reselect';
import { maxBy, values, orderBy, findIndex } from 'lodash';
import Moment from 'moment';

import { dispatchersActionTypes } from './../actions/types';
import { cachedRequestByIdReducer } from './helpers';
import { utils } from './../helpers';
import { getMetricsByDateTimestamp } from './metricsByDateInfo';
import { getDispatcherType } from './regressionPage';
import { getJobNameNumber } from './../helpers/job';

const dispatchers = cachedRequestByIdReducer(dispatchersActionTypes);
export default dispatchers;

const getDispatcherState = state => state.dispatchers;
export const dispatchersById = state => getDispatcherState(state).byId;
export const getDispatcherById = (state, id) => getDispatcherState(state).byId[id];

export const getDispatchers = createSelector(
  getDispatcherState,
  (dispatchers) => values(dispatchers.byId)
);

export const groupDispatchersByType = (dispatchers) =>
  utils.propertyAccumulatorReducer(dispatchers, 'dispatcher_type');

const dispatchersByTypes = createSelector(
  getDispatchers,
  (dispatchers) => groupDispatchersByType(dispatchers)
);

export const cachedDispatchersByType = createCachedSelector(
  dispatchersByTypes,
  (_state, type) => type,
  (dispatchersByType, type) => {
    const dispatchers = dispatchersByType && dispatchersByType[type];
    if (dispatchers === undefined)
    {
      return [];
    }
    return dispatchers;
  }
)(
  (_state, type) => type
);


const dispatchersByTypesOrderByDate = createSelector(
  dispatchersByTypes,
  (dispatchersByTypes) => values(dispatchersByTypes).reduce(
    (result, dispatchers) => {
      if (dispatchers.length === 0) {
        return;
      }

      result[dispatchers[0].dispatcher_type] =
        orderBy(dispatchers, d => d.created_at, 'desc');
      return result;
    }, {}
  )
);

export const dispatchersByTypesAfterCaseMetricsDate = createSelector(
  dispatchersByTypesOrderByDate,
  getMetricsByDateTimestamp,
  (dispatchersByTypes, timestamp) => values(dispatchersByTypes).reduce(
    (result, dispatchers) => {
      if (dispatchers.length === 0) { return; }

      const passedDateIndex = findIndex(
        dispatchers,
        d => Moment(d.created_at).unix() < timestamp
      );

      result[dispatchers[0].dispatcher_type] =
        dispatchers.slice(0, passedDateIndex);
      return result;
    }, {}
  )
);

export const getDispatchersByTypesAfterCaseMetricsDate = (state, type) =>
  dispatchersByTypesAfterCaseMetricsDate(state)[type];

const maxScenarioCountByDispatcherTypes = createSelector(
  dispatchersByTypes,
  (dispatchersByTypes) => values(dispatchersByTypes).reduce(
    (result, dispatchers) => {
      if (dispatchers.length === 0) {
        return;
      }

      result[dispatchers[0].dispatcher_type] =
        maxBy(dispatchers, d => d.scenario_count).scenario_count;
      return result;
    }, {}
  )
);
export const getMaxScenarioCountByDispatcherType = (state, dispatcherType) =>
  maxScenarioCountByDispatcherTypes(state)[dispatcherType];

export const findLatestDispatcherFromTypeGroups = (dispatchersByType) =>
  values(dispatchersByType).reduce((result, ds) => {
    const latest = maxBy(ds, d => d.created_at);
    result[latest.dispatcher_type] = latest;
    return result;
  }, {});
const getLatestDispatcherByTypes = createSelector(
  dispatchersByTypes,
  (dispatchersByType) => findLatestDispatcherFromTypeGroups(dispatchersByType)
);
export const getLatestDispatcherByType = (state, type) =>
  getLatestDispatcherByTypes(state)[type];

export const dispatchersByJobIds = createSelector(
  getDispatchers,
  (dispatchers) => utils.propertyAccumulatorReducer(dispatchers, 'job_id')
);
export const getDispatchersByJobId = (state, jobId) =>
  dispatchersByJobIds(state)[jobId];

export const regressionPageDispatchersByJobIds = createSelector(
  (state) => cachedDispatchersByType(state, getDispatcherType(state)),
  (dispatchers) => utils.propertyAccumulatorReducer(dispatchers, 'job_id')
);
export const getRegressionPageDispatchersByJobId = (state, jobId) =>
  regressionPageDispatchersByJobIds(state)[jobId];

export const getDispatcherNameNumberJobIdPairs = createSelector(
  (state) => cachedDispatchersByType(state, getDispatcherType(state)),
  (dispatchers) => dispatchers.reduce(
    (result, dispatcher) => {
      if (!dispatcher) {
        return;
      }
      const job = dispatcher.job;
      if (job)
      {
        result[getJobNameNumber(job)] = job.id;
      }
      return result;
    }, {}
  )
);

export const getJobIdByDispatcherNameNumber = (state, dispatcherNameNumber) =>
  getDispatcherNameNumberJobIdPairs(state)[dispatcherNameNumber];


// WEBPACK FOOTER //
// src/reducers/dispatchers.js